import React from 'react';
import {Disclosure} from '@headlessui/react';
import clsx from 'clsx';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useMatches,
} from '@remix-run/react';

//domains
import {
  AVAILABILITY_FILTER_ID,
  AVAILABILITY_ALL,
  PRICE_FILTER_ID,
  SIZE_OPTION_FILTER_ID,
  STOCK_STATUS_FILTER_ID,
  STOCK_STATUS_IN_STOCK_VALUE,
  STOCK_STATUS_OUT_OF_STOCK_VALUE,
  stockStatusFilterInput,
  FILTER_PARAM_PREFIX,
  mergeSizeValues,
  type DisplayFacetValue,
  type NativeFilter,
  type NativeFilterValue,
  type NativePriceBounds,
} from 'domains/nativeCollection';
import {loader} from '~/routes/($lang)/collections/$collectionHandle';
import type {I18nLocale} from '~/lib/type';

//components
import {Slider} from './RangeSlider';
import {MODEL_FACET_ID, groupBySeries} from '~/lib/brandModels';

/**
 * Sidebar for the native Shopify filter facets (Search & Discovery config).
 * URL contract: each facet's param key is its facet id and the value is the
 * facet value's `input` JSON verbatim — the loader parses them straight into
 * the ProductFilter list. Availability keeps the site default (in-stock only)
 * until the shopper explicitly changes it.
 */

/**
 * Metafield facet headings come back in English (or a different string per
 * language context), so map by the locale-independent facet id — robust
 * across zh-TW, zh-CN and any future locale. (Traditional strings here are
 * converted to Simplified on the /zh-cn locale by SimplifiedChinese.)
 */
const FACET_LABEL_ZH: Record<string, string> = {
  [MODEL_FACET_ID]: '型號',
  'filter.p.product_type': '產品類型',
  [STOCK_STATUS_FILTER_ID]: '供貨情況',
  'filter.v.option.size': '尺碼',
  'filter.p.m.custom.helmet_type': '頭盔類型',
  'filter.p.m.custom.helmet_certs': '安全認證',
  'filter.p.m.custom.weight_grams': '重量（克）',
  'filter.p.m.custom.pinlock_ready': '支援 Pinlock 防霧鏡片',
  'filter.p.m.custom.comms_ready': '支援藍牙耳機',
};

/**
 * English labels for facets whose Shopify label is authored in Chinese (custom
 * metafield facets aren't translated in admin), so the /en storefront would
 * otherwise show Chinese headings/values.
 */
const FACET_LABEL_EN: Record<string, string> = {
  [STOCK_STATUS_FILTER_ID]: 'Availability',
  [MODEL_FACET_ID]: 'Model',
};

const STOCK_STATUS_VALUE_EN: Record<string, string> = {
  [STOCK_STATUS_IN_STOCK_VALUE]: 'In Stock',
  [STOCK_STATUS_OUT_OF_STOCK_VALUE]: 'Out of Stock',
};

const FACET_VALUE_ZH: Record<string, string> = {
  // custom.helmet_type metafield facet values
  'Full Face': '全面盔',
  'Open Face 3/4': '3/4 開面盔',
  Modular: '揭面盔',
  'Off-Road': '越野盔',
  // Product type facet values — Shopify's internal taxonomy is English-only
  // ("GROUP - SUBTYPE") and not translatable in admin, so map every value in
  // the store here. Traditional strings convert to Simplified on /zh-cn;
  // unlisted types fall back to their English label.
  'HELMET - FULL FACE': '頭盔 - 全面盔',
  'HELMET - OPEN FACE(3/4)': '頭盔 - 3/4 開面盔',
  'HELMET - MODULAR': '頭盔 - 揭面盔',
  'HELMET - DIRT': '頭盔 - 越野盔',
  'HELMET - DUAL SPORT': '頭盔 - 兩用盔',
  'HELMET - VISOR': '頭盔 - 鏡片',
  'HELMET - LINERS': '頭盔 - 內襯',
  'HELMET - OTHER ACCESSORIES': '頭盔 - 其他配件',
  'HELMET - BLUETOOTH COMMUNICATORS & INTERCOMS': '頭盔 - 藍牙通訊耳機',
  'RIDER GEARS - GLOVES': '騎士裝備 - 手套',
  'RIDER GEARS - JACKETS': '騎士裝備 - 外套',
  'RIDER GEARS - PANTS/JEANS': '騎士裝備 - 褲／牛仔褲',
  'RIDER GEARS - BOOTS/SHOES': '騎士裝備 - 鞋靴',
  'RIDER GEARS - PROTECTION': '騎士裝備 - 護具',
  'RIDER GEARS - RAIN GEAR': '騎士裝備 - 雨具',
  'RIDER GEARS - CASUAL T-SHIRT': '騎士裝備 - 休閒 T 恤',
  'RIDER GEARS - OTHER CLOTHING/WEAR': '騎士裝備 - 其他服飾',
  'ACCESSORIES - LUGGAGE & BAG': '配件 - 行李及袋',
  'ACCESSORIES - BIKE CLEANING & CARE': '配件 - 清潔及保養',
  'ACCESSORIES - OTHER MOTORCYCLE GOODS': '配件 - 其他電單車用品',
  'ACCESSORIES - PHONE ACCESSORIES': '配件 - 手機配件',
  'ACCESSORIES - PHONE MOUNTS': '配件 - 手機支架',
  'ACCESSORIES - LOCK & SECURITY': '配件 - 鎖具及防盜',
  'MOTORCYCLE PARTS - BATTERIES & ELECTRICAL': '電單車部件 - 電池及電器',
  'MOTORCYCLE PARTS - BODY & FRAMES': '電單車部件 - 車身及車架',
  'MOTORCYCLE PARTS - BRAKE & SUSPENSION': '電單車部件 - 剎車及避震',
  'MOTORCYCLE PARTS - CASE & RACK': '電單車部件 - 尾箱及尾架',
  'MOTORCYCLE PARTS - CHAIN & SPROCKETS': '電單車部件 - 鏈條及齒盤',
  'MOTORCYCLE PARTS - ENGINE & TRANSMISSION': '電單車部件 - 引擎及傳動',
  'MOTORCYCLE PARTS - EXHAUST': '電單車部件 - 死氣喉',
  'MOTORCYCLE PARTS - FILTER & CONSUMABLE MATERIAL':
    '電單車部件 - 濾清器及耗材',
  'MOTORCYCLE PARTS - HANDLING & FOOT CONTROL': '電單車部件 - 手把及腳踏',
  'MOTORCYCLE PARTS - MIRRORS': '電單車部件 - 倒後鏡',
  'MOTORCYCLE PARTS - MOTO CAM': '電單車部件 - 行車記錄儀',
  'MOTORCYCLE PARTS - TIRE & WHEEL': '電單車部件 - 輪胎及車輪',
  'SERVICES & EVENTS': '服務及活動',
};

/** Display order — unknown facets keep API order in the middle. */
const FACET_ORDER: Record<string, number> = {
  // 型號 leads where it renders: it is the primary axis of a brand page.
  // Shopify returns the facet on every collection once the Search & Discovery
  // filter is on, so CollectionMain hides it everywhere except brand pages
  // rather than letting one brand's model list outrank 產品類型 and 品牌.
  [MODEL_FACET_ID]: -1,
  'filter.p.product_type': 0,
  [AVAILABILITY_FILTER_ID]: 1,
  [STOCK_STATUS_FILTER_ID]: 1,
  'filter.p.vendor': 2,
  'filter.v.option.size': 3,
  'filter.p.m.custom.helmet_type': 4,
  'filter.p.m.custom.helmet_certs': 5,
  'filter.p.m.custom.pinlock_ready': 6,
  'filter.p.m.custom.comms_ready': 7,
  'filter.p.m.custom.weight_grams': 98,
  // Price kept at the very bottom of the sidebar.
  [PRICE_FILTER_ID]: 99,
};

const COLLAPSED_BY_DEFAULT = new Set(['filter.p.m.custom.weight_grams']);

function useIsZh() {
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale as I18nLocale | undefined;
  return selectedLocale?.language !== 'EN';
}

function useFilterNavigation() {
  const location = useLocation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  // While a filter navigation is in flight, read state from the PENDING URL
  // so checkboxes, the slider and the clear button respond instantly instead
  // of waiting for the loader round-trip.
  const pendingSearch =
    navigation.location && navigation.location.pathname === location.pathname
      ? navigation.location.search
      : location.search;
  const params = React.useMemo(
    () => new URLSearchParams(pendingSearch),
    [pendingSearch],
  );
  const apply = React.useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      next.set('page', '1');
      // Keep the shopper's scroll position — jumping to the top on every
      // checkbox reads as a full page reload.
      navigate(`${location.pathname}?${next.toString()}`, {
        preventScrollReset: true,
      });
    },
    [params, navigate, location.pathname],
  );
  return {params, apply};
}

/**
 * One facet value as one sidebar row.
 *
 * The key is the value's `input`, never Shopify's `id`: Shopify derives that
 * id by slugifying the label, and the slug drops punctuation, so SHOEI's J.O
 * and J.O+ both arrive as `filter.p.m.custom.model.j-o`. Keying on it handed
 * React two children with one key — the sidebar rendered the same row eleven
 * times — and made the two indistinguishable wherever a row was looked up by
 * key, so an empty J.O borrowed J.O+'s four products and showed as "J.O (0)"
 * instead of being dropped. The input JSON carries the value verbatim, is
 * what the URL already round-trips, and is unique by construction.
 *
 * Every place that turns a facet value into a row goes through here, so the
 * two sides of a key comparison can never drift apart.
 */
function toDisplayValue(v: NativeFilterValue): DisplayFacetValue {
  return {
    key: v.input,
    label: v.label,
    count: v.count ?? 0,
    inputs: [v.input],
  };
}

/**
 * Rows a list facet renders: the size facet merges synonymous conventions
 * ("2XL"/"XXL", "M [59-60cm]"/"M") into single rows carrying every raw
 * input; other facets map 1:1. Zero-count rows are dropped unless selected,
 * so shoppers never see un-clickable "(0)" noise but can always un-tick.
 */
function displayValuesFor(
  facet: NativeFilter,
  applied: string[],
): DisplayFacetValue[] {
  let values: DisplayFacetValue[];
  if (facet.id === SIZE_OPTION_FILTER_ID) {
    values = mergeSizeValues(facet.values);
  } else {
    values = facet.values.map(toDisplayValue);
    if (facet.id === 'filter.p.m.custom.weight_grams') {
      values = [...values].sort(
        (a, b) => parseFloat(a.label) - parseFloat(b.label),
      );
    }
  }
  return values.filter(
    (v) => v.count > 0 || v.inputs.some((input) => applied.includes(input)),
  );
}

export default function NativeCollectionFilter({
  hideFacets,
}: {
  /** Facet ids to omit — e.g. the vendor facet on brand pages, where the
   * brand IS the page and offering other brands would be noise. */
  hideFacets?: string[];
}) {
  const {nativeFilters} = useLoaderData<typeof loader>();
  const isZh = useIsZh();
  const {params, apply} = useFilterNavigation();

  if (!nativeFilters) return null;

  const facets = [...nativeFilters.facets]
    .filter((f) => !hideFacets?.includes(f.id))
    .sort((a, b) => (FACET_ORDER[a.id] ?? 50) - (FACET_ORDER[b.id] ?? 50));

  const appliedFilterEntries = Array.from(params.keys()).filter((k) =>
    k.startsWith(FILTER_PARAM_PREFIX),
  ).length;

  return (
    <div className="mobile-menu h-full overflow-hidden overflow-y-scroll px-5 pb-20 md:px-0 md:pb-0">
      <div className="grid gap-[26px]">
        {appliedFilterEntries > 0 && (
          <button
            onClick={() =>
              apply((next) => {
                for (const key of Array.from(next.keys())) {
                  if (key.startsWith(FILTER_PARAM_PREFIX)) next.delete(key);
                }
              })
            }
            className="flex items-center justify-between border-[1px] border-solid border-black px-3 py-2 text-sm"
          >
            <span>{isZh ? '清除所有篩選' : 'Clear all filters'}</span>
            <ClearIcon />
          </button>
        )}
        {facets.map((facet) => {
          if (!facet.values || facet.values.length === 0) return null;

          const isPrice = facet.id === PRICE_FILTER_ID;
          if (isPrice) {
            const bounds = nativeFilters.priceBounds;
            if (!bounds || bounds.min === bounds.max) return null;
          }

          const isAvailability = facet.id === AVAILABILITY_FILTER_ID;
          const isStockStatus = facet.id === STOCK_STATUS_FILTER_ID;
          const displayValues =
            isPrice || isAvailability || isStockStatus
              ? null
              : displayValuesFor(facet, params.getAll(facet.id));
          if (displayValues && displayValues.length === 0) return null;

          const label = isZh
            ? FACET_LABEL_ZH[facet.id] ?? facet.label
            : FACET_LABEL_EN[facet.id] ?? facet.label;

          return (
            <div
              key={facet.id}
              className="border-b-[1px] border-solid border-black"
            >
              <Disclosure defaultOpen={!COLLAPSED_BY_DEFAULT.has(facet.id)}>
                {({open}) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-between pt-2 pb-4 text-left">
                      <div className="">{label}</div>
                      {open ? <MinusIcon /> : <PlusIcon />}
                    </Disclosure.Button>
                    <Disclosure.Panel
                      className={clsx([
                        'text-gray-500 my-2',
                        (displayValues?.length ?? 0) > 9 &&
                          'md:max-h-[400px] md:overflow-scroll',
                      ])}
                    >
                      {isAvailability ? (
                        <AvailabilityFacet facet={facet} />
                      ) : isStockStatus ? (
                        <StockStatusFacet facet={facet} isZh={isZh} />
                      ) : isPrice ? (
                        <PriceFacet
                          bounds={
                            nativeFilters.priceBounds as NativePriceBounds
                          }
                        />
                      ) : facet.id === MODEL_FACET_ID ? (
                        <ModelFacet
                          facet={facet}
                          values={displayValues as DisplayFacetValue[]}
                          isZh={isZh}
                        />
                      ) : (
                        <ListFacet
                          facetId={facet.id}
                          values={displayValues as DisplayFacetValue[]}
                          isZh={isZh}
                        />
                      )}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListFacet({
  facetId,
  values,
  isZh,
}: {
  facetId: string;
  values: DisplayFacetValue[];
  isZh: boolean;
}) {
  const {params, apply} = useFilterNavigation();
  const applied = params.getAll(facetId);

  return (
    <div className="grid gap-3">
      {values.map((value) => {
        const isActive = value.inputs.some((input) => applied.includes(input));
        const label = isZh
          ? FACET_VALUE_ZH[value.label] ?? value.label
          : value.label;
        return (
          <FacetCheckboxRow
            key={value.key}
            checked={isActive}
            label={`${label} (${value.count})`}
            onClick={() =>
              apply((next) => {
                const remaining = next
                  .getAll(facetId)
                  .filter((input) => !value.inputs.includes(input));
                next.delete(facetId);
                if (!isActive) remaining.push(...value.inputs);
                remaining.forEach((input) => next.append(facetId, input));
              })
            }
          />
        );
      })}
    </div>
  );
}

/**
 * The 型號 facet, folded by series: EXO-1400, EXO-1400 AIR and EXO-1400 EVO
 * AIR sit under one "EXO-1400 系列" row whose checkbox ticks or clears every
 * member at once, with the members themselves behind a chevron. A series
 * with a single listed model renders flat — a group of one is only noise.
 *
 * The URL contract is untouched: the series row is shorthand for appending
 * each member's input, and the loader OR's them exactly as if the shopper
 * had ticked them one by one. Selecting a whole series therefore selects
 * several models, so the model band above the grid (which needs exactly
 * one) stays hidden until the shopper narrows to a single model.
 */
function ModelFacet({
  facet,
  values,
  isZh,
}: {
  facet: NativeFilter;
  /** The rows to show — zero-count models already dropped. */
  values: DisplayFacetValue[];
  isZh: boolean;
}) {
  const {params, apply} = useFilterNavigation();
  const facetId = facet.id;
  const applied = params.getAll(facetId);
  const isActive = (value: DisplayFacetValue) =>
    value.inputs.some((input) => applied.includes(input));
  const setMembers = (members: DisplayFacetValue[], select: boolean) =>
    apply((next) => {
      const inputs = members.flatMap((member) => member.inputs);
      const remaining = next
        .getAll(facetId)
        .filter((input) => !inputs.includes(input));
      next.delete(facetId);
      if (select) remaining.push(...inputs);
      remaining.forEach((input) => next.append(facetId, input));
    });

  // A series is composed from every model Shopify returns, not just the rows
  // on screen: a model with no products under the shopper's other filters is
  // hidden, and if the series row skipped it, ticking 系列 then widening
  // another facet would surface an unticked member and drop the series to a
  // half-selected state. Its input matches nothing while it is empty, so
  // carrying it costs nothing and keeps the selection whole.
  const allValues = facet.values.map(toDisplayValue);
  const visible = new Set(values.map((value) => value.key));

  return (
    <div className="grid gap-3">
      {groupBySeries(allValues).map((group) => {
        const shown = group.members.filter((member) => visible.has(member.key));
        if (shown.length === 0) return null;
        // One row on screen is not a group worth folding, whatever the
        // series holds in the catalogue.
        if (shown.length === 1) {
          const value = shown[0];
          const active = isActive(value);
          return (
            <FacetCheckboxRow
              key={value.key}
              checked={active}
              label={`${value.label} (${value.count})`}
              onClick={() => setMembers([value], !active)}
            />
          );
        }
        return (
          <ModelSeriesGroup
            key={group.series}
            series={group.series}
            members={group.members}
            shown={shown}
            isZh={isZh}
            isActive={isActive}
            setMembers={setMembers}
          />
        );
      })}
    </div>
  );
}

function ModelSeriesGroup({
  series,
  members,
  shown,
  isZh,
  isActive,
  setMembers,
}: {
  series: string;
  /** Every model in the series, including any the filters have emptied. */
  members: DisplayFacetValue[];
  /** The subset with products to show right now. */
  shown: DisplayFacetValue[];
  isZh: boolean;
  isActive: (value: DisplayFacetValue) => boolean;
  setMembers: (members: DisplayFacetValue[], select: boolean) => void;
}) {
  const activeCount = members.filter(isActive).length;
  const allActive = activeCount === members.length;
  // The URL drives the fold: a group whose member the URL already picks
  // arrives open, so the ticked model is visible rather than buried, and one
  // with nothing picked starts folded, which is the whole point of grouping.
  // Only a shopper's own tap on the chevron overrides that, and it holds
  // until they clear the series — a group left folded on a narrow viewport
  // must not stay folded over a selection made in the mobile panel.
  const [override, setOverride] = React.useState<boolean | null>(null);
  const open = override ?? activeCount > 0;
  const panelId = React.useId();
  // Facet counts are per value, and a product listing two models of the
  // same series is counted under each — so this is the ceiling on what the
  // series row will show, not always the exact grid size.
  const total = members.reduce((sum, member) => sum + member.count, 0);

  return (
    <div className="grid gap-3">
      <div className="flex items-start justify-between gap-2">
        <FacetCheckboxRow
          checked={allActive}
          indeterminate={activeCount > 0 && !allActive}
          label={
            isZh ? `${series} 系列 (${total})` : `${series} series (${total})`
          }
          onClick={() => {
            setMembers(members, !allActive);
            // Clearing the series retires the shopper's fold state with it.
            if (allActive) setOverride(null);
          }}
        />
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOverride(!open)}
          className="flex h-6 w-6 shrink-0 items-center justify-center"
        >
          {/* The name goes in the DOM rather than in aria-label: the /zh-cn
              locale converts text nodes, not attributes, so an attribute
              would be announced in Traditional under a zh-Hans document. */}
          <span className="sr-only">
            {isZh
              ? `${open ? '收起' : '展開'} ${series} 系列型號`
              : `${open ? 'Hide' : 'Show'} ${series} models`}
          </span>
          <ChevronIcon open={open} />
        </button>
      </div>
      {open && (
        <div id={panelId} className="grid gap-3 pl-6">
          {shown.map((value) => {
            const active = isActive(value);
            return (
              <FacetCheckboxRow
                key={value.key}
                checked={active}
                label={`${value.label} (${value.count})`}
                onClick={() => setMembers([value], !active)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Availability is a pair of checkboxes over {in-stock, out-of-stock} where
 * the site default is in-stock only. URL states: no param = default,
 * `all` = both, {"available":false} = out-of-stock only.
 */
function AvailabilityFacet({facet}: {facet: NativeFilter}) {
  const {params, apply} = useFilterNavigation();
  const rawValues = params.getAll(AVAILABILITY_FILTER_ID);

  const showAll = rawValues.includes(AVAILABILITY_ALL);
  const explicit = rawValues
    .map((v) => {
      try {
        return (JSON.parse(v) as {available?: boolean}).available;
      } catch {
        return undefined;
      }
    })
    .filter((v): v is boolean => typeof v === 'boolean');

  // No param OR an invalid/mangled value ⇒ the loader keeps the in-stock
  // default, so mirror that here instead of rendering both boxes unchecked.
  const inStockChecked =
    showAll || explicit.includes(true) || (!showAll && explicit.length === 0);
  const outOfStockChecked = showAll || explicit.includes(false);

  const setState = (inStock: boolean, outOfStock: boolean) =>
    apply((next) => {
      next.delete(AVAILABILITY_FILTER_ID);
      if (inStock && outOfStock) {
        next.append(AVAILABILITY_FILTER_ID, AVAILABILITY_ALL);
      } else if (!inStock && outOfStock) {
        next.append(AVAILABILITY_FILTER_ID, JSON.stringify({available: false}));
      }
      // inStock only = site default = no param
    });

  return (
    <div className="grid gap-3">
      {facet.values.map((value) => {
        let isInStockValue = false;
        try {
          isInStockValue =
            (JSON.parse(value.input) as {available?: boolean}).available ===
            true;
        } catch {
          return null;
        }
        const checked = isInStockValue ? inStockChecked : outOfStockChecked;
        return (
          <FacetCheckboxRow
            key={value.id}
            checked={checked}
            label={`${value.label} (${value.count})`}
            onClick={() => {
              let nextIn = isInStockValue ? !inStockChecked : inStockChecked;
              let nextOut = isInStockValue
                ? outOfStockChecked
                : !outOfStockChecked;
              if (!nextIn && !nextOut) {
                // never allow an empty state — flip to the other value
                nextIn = !isInStockValue;
                nextOut = isInStockValue;
              }
              setState(nextIn, nextOut);
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * The store's availability facet is a custom metafield (`custom.stock_status`
 * = 有庫存 / 無庫存), not Shopify's native Availability facet (disabled here).
 * Same UX as AvailabilityFacet: no param = the loader's in-stock default
 * (有庫存 checked, 無庫存 unchecked); both checked = show everything; 無庫存
 * only = out-of-stock only. An empty selection is never allowed.
 */
function StockStatusFacet({facet, isZh}: {facet: NativeFilter; isZh: boolean}) {
  const {params, apply} = useFilterNavigation();
  const selected = params
    .getAll(STOCK_STATUS_FILTER_ID)
    .map((v) => {
      try {
        return (JSON.parse(v) as {productMetafield?: {value?: string}})
          .productMetafield?.value;
      } catch {
        return undefined;
      }
    })
    .filter((v): v is string => typeof v === 'string');

  const hasExplicit = selected.length > 0;
  // No explicit choice ⇒ mirror the loader's in-stock default.
  const inStockChecked =
    !hasExplicit || selected.includes(STOCK_STATUS_IN_STOCK_VALUE);
  const outOfStockChecked = selected.includes(STOCK_STATUS_OUT_OF_STOCK_VALUE);

  const setState = (inStock: boolean, outOfStock: boolean) =>
    apply((next) => {
      next.delete(STOCK_STATUS_FILTER_ID);
      if (inStock && outOfStock) {
        // Both values are OR'd by the Storefront API = every product.
        next.append(
          STOCK_STATUS_FILTER_ID,
          stockStatusFilterInput(STOCK_STATUS_IN_STOCK_VALUE),
        );
        next.append(
          STOCK_STATUS_FILTER_ID,
          stockStatusFilterInput(STOCK_STATUS_OUT_OF_STOCK_VALUE),
        );
      } else if (!inStock && outOfStock) {
        next.append(
          STOCK_STATUS_FILTER_ID,
          stockStatusFilterInput(STOCK_STATUS_OUT_OF_STOCK_VALUE),
        );
      }
      // in-stock only = the site default = no param
    });

  return (
    <div className="grid gap-3">
      {facet.values.map((value) => {
        let raw: string | undefined;
        try {
          raw = (
            JSON.parse(value.input) as {productMetafield?: {value?: string}}
          ).productMetafield?.value;
        } catch {
          return null;
        }
        if (
          raw !== STOCK_STATUS_IN_STOCK_VALUE &&
          raw !== STOCK_STATUS_OUT_OF_STOCK_VALUE
        ) {
          return null;
        }
        const isInStockValue = raw === STOCK_STATUS_IN_STOCK_VALUE;
        const checked = isInStockValue ? inStockChecked : outOfStockChecked;
        const label = isZh
          ? value.label
          : STOCK_STATUS_VALUE_EN[value.label] ?? value.label;
        return (
          <FacetCheckboxRow
            key={value.input}
            checked={checked}
            label={`${label} (${value.count})`}
            onClick={() => {
              let nextIn = isInStockValue ? !inStockChecked : inStockChecked;
              let nextOut = isInStockValue
                ? outOfStockChecked
                : !outOfStockChecked;
              if (!nextIn && !nextOut) {
                // never allow an empty state — flip to the other value
                nextIn = !isInStockValue;
                nextOut = isInStockValue;
              }
              setState(nextIn, nextOut);
            }}
          />
        );
      })}
    </div>
  );
}

function PriceFacet({bounds}: {bounds: NativePriceBounds}) {
  const {params, apply} = useFilterNavigation();

  const appliedRaw = params.get(PRICE_FILTER_ID);
  let appliedMin = bounds.min;
  let appliedMax = bounds.max;
  if (appliedRaw) {
    try {
      const parsed = JSON.parse(appliedRaw) as {
        price?: {min?: number; max?: number};
      };
      if (typeof parsed?.price?.min === 'number') appliedMin = parsed.price.min;
      if (typeof parsed?.price?.max === 'number') appliedMax = parsed.price.max;
    } catch {
      // malformed param — treat as unfiltered
    }
  }

  const [minPrice, setMinPrice] = React.useState(appliedMin);
  const [maxPrice, setMaxPrice] = React.useState(appliedMax);

  React.useEffect(() => {
    setMinPrice(appliedMin);
    setMaxPrice(appliedMax);
  }, [appliedMin, appliedMax]);

  return (
    <div className="px-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="">{minPrice.toFixed(2)}</span>
        <span className="">&mdash;</span>
        <span className="">{maxPrice.toFixed(2)}</span>
      </div>
      <div className="mb-4">
        <Slider
          min={bounds.min}
          max={bounds.max}
          defaultValue={[minPrice, maxPrice]}
          value={[minPrice, maxPrice]}
          onValueCommit={(priceRange: number[]) => {
            const [min, max] = priceRange;
            apply((next) => {
              next.delete(PRICE_FILTER_ID);
              if (min > bounds.min || max < bounds.max) {
                next.append(
                  PRICE_FILTER_ID,
                  JSON.stringify({price: {min, max}}),
                );
              }
            });
          }}
          onValueChange={(priceRange: number[]) => {
            setMinPrice(priceRange[0] as number);
            setMaxPrice(priceRange[1] as number);
          }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-slate-400 opacity-75">
        <span className="">{bounds.min.toFixed(2)}</span>
        <span className="">{bounds.max.toFixed(2)}</span>
      </div>
    </div>
  );
}

function FacetCheckboxRow({
  checked,
  indeterminate = false,
  label,
  onClick,
}: {
  checked: boolean;
  /** Some but not all of what this row stands for is applied (series rows). */
  indeterminate?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      onClick={onClick}
      className=""
    >
      <div className="flex cursor-pointer items-center space-x-2">
        <div className="flex h-[15px] w-[15px] shrink-0 items-center justify-center border-[1px] border-solid border-black p-[2px]">
          {checked ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
              viewBox="0 0 24 24"
            >
              <path d="M9 22l-10-10.598 2.798-2.859 7.149 7.473 13.144-14.016 2.909 2.806z" />
            </svg>
          ) : indeterminate ? (
            // A border, not a filled block: forced-colors mode (Windows High
            // Contrast) paints backgrounds over but keeps border colours, so
            // a bg-black bar would vanish exactly where the tick survives.
            <div aria-hidden className="h-0 w-full border-t-2 border-black" />
          ) : null}
        </div>
        <div className="cursor-pointer font-light text-left">{label}</div>
      </div>
    </button>
  );
}

function ChevronIcon({open}: {open: boolean}) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={clsx('transition-transform', open && 'rotate-180')}
    >
      <path
        d="M1.5 4L6 8.5L10.5 4"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 9.35849L1.85849 15L0 13.1415L5.64151 7.5L0 1.85849L1.85849 0L7.5 5.64151L13.1415 0L15 1.85849L9.35849 7.5L15 13.1415L13.1415 15L7.5 9.35849Z"
        fill="black"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="13"
      height="3"
      viewBox="0 0 13 3"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.0851 0.659999V2.816H0.913063V0.659999H12.0851Z"
        fill="black"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.31536 0.611999V6.688H0.37936V8.844H6.31536V14.892H8.69536V8.844H14.6594V6.688H8.69536V0.611999H6.31536Z"
        fill="black"
      />
    </svg>
  );
}
